import { Clock, Instagram, Mail, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { SHOW_SISTEMA_LUCENTIA } from "@/config/features";
import { BLOG_POSTS } from "@/blog/data";
import { Logo } from "@/components/brand/Logo";
import { WhatsAppIcon } from "@/components/icons/WhatsAppIcon";
import { SOCIAL } from "@/config/constants";
import {
  LEGAL_ADDRESS,
  LEGAL_CONTACT_EMAIL,
  LEGAL_MAPS_URL,
  LEGAL_PHONE,
} from "@/data/legal.types";
import { useContent } from "@/data/content";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import { trackEvent } from "@/utils/analytics";
import { resetCookieConsent } from "@/utils/cookieConsent";
import { LanguageToggle } from "./LanguageToggle";

const year = new Date().getFullYear();

const linkCls =
  "inline-flex min-h-8 items-center text-base text-on-navy-muted transition-colors hover:text-white";

export function Footer() {
  const { t: tNav } = useAppTranslation("nav");
  const { t: tCommon } = useAppTranslation("common");
  const services = useContent().services;

  const legal = [
    { to: "/aviso-legal", label: tNav("legalNotice") },
    { to: "/privacidad", label: tNav("privacy") },
    { to: "/cookies", label: tNav("cookies") },
    { to: "/condiciones", label: tNav("terms") },
    { to: "/accesibilidad", label: tNav("accessibility") },
  ];

  return (
    <footer aria-labelledby="footer-heading" className="on-navy relative overflow-hidden bg-navy-deep text-white">
      <h2 id="footer-heading" className="sr-only">
        {tNav("footerHeading")}
      </h2>
      <div aria-hidden="true" className="light-pool-navy pointer-events-none absolute inset-0 opacity-60 [--pool-x:85%] [--pool-y:0%]" />

      <div className="relative mx-auto max-w-7xl px-4 pb-28 pt-16 sm:px-6 lg:px-8 lg:pt-20">
        <div className="grid gap-12 lg:grid-cols-[1.3fr_1fr_1fr_1.3fr]">
          <div className="max-w-xs">
            <Link to="/" aria-label={tCommon("homeLogoAria")} className="inline-block rounded-lg">
              <Logo layout="stacked" tone="light" alt="" className="w-[150px]" />
            </Link>
            <p className="mt-6 text-base leading-relaxed text-on-navy-muted">{tNav("footerTagline")}</p>
            <p className="mt-4 font-display text-lg text-teal-light">{tNav("pillars")}</p>
          </div>

          <nav aria-label={tNav("footerServices")}>
            <h3 className="text-sm font-semibold text-white">{tNav("footerServices")}</h3>
            <ul className="mt-4 space-y-1.5">
              {services.map((service) => (
                <li key={service.href}>
                  <Link to={service.href ?? "/"} className={linkCls}>
                    {service.title}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/precios" className={linkCls}>
                  {tNav("pricing")}
                </Link>
              </li>
            </ul>
          </nav>

          <nav aria-label={tNav("footerStudio")}>
            <h3 className="text-sm font-semibold text-white">{tNav("footerStudio")}</h3>
            <ul className="mt-4 space-y-1.5">
              {SHOW_SISTEMA_LUCENTIA ? (
                <li>
                  <Link to="/metodo" className={linkCls}>
                    Sistema Lucentia®
                  </Link>
                </li>
              ) : null}
              <li>
                <Link to="/#sobre-mi" className={linkCls}>
                  {tNav("about")}
                </Link>
              </li>
              <li>
                <Link to="/#contacto" className={linkCls}>
                  {tNav("contact")}
                </Link>
              </li>
              {BLOG_POSTS.length ? (
                <li>
                  <Link to="/blog" className={linkCls}>
                    {tNav("blog")}
                  </Link>
                </li>
              ) : null}
              <li>
                <Link to="/recursos" className={linkCls}>
                  {tNav("freeGuide")}
                </Link>
              </li>
              <li>
                <Link to="/#faq" className={linkCls}>
                  {tNav("faq")}
                </Link>
              </li>
            </ul>
          </nav>

          <div>
            <h3 className="text-sm font-semibold text-white">{tNav("footerContact")}</h3>
            <ul className="mt-4 space-y-3 text-base text-on-navy-muted">
              <li>
                <a
                  href={SOCIAL.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-tracked=""
                  onClick={() => trackEvent("whatsapp_click", { location: "footer" })}
                  className="inline-flex items-center gap-3 transition-[color,translate] duration-300 hover:text-white motion-safe:hover:translate-x-0.5"
                >
                  <WhatsAppIcon className="size-4 shrink-0 text-teal-light" />
                  <span>
                    WhatsApp {LEGAL_PHONE}
                    <span className="sr-only"> {tCommon("opensNewTab")}</span>
                  </span>
                </a>
              </li>
              <li>
                <a href={`mailto:${LEGAL_CONTACT_EMAIL}`} className="inline-flex items-center gap-3 break-all transition-[color,translate] duration-300 hover:text-white motion-safe:hover:translate-x-0.5">
                  <Mail aria-hidden="true" className="size-4 shrink-0 text-teal-light" />
                  {LEGAL_CONTACT_EMAIL}
                </a>
              </li>
              <li>
                <a
                  href={SOCIAL.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 transition-[color,translate] duration-300 hover:text-white motion-safe:hover:translate-x-0.5"
                >
                  <Instagram aria-hidden="true" className="size-4 shrink-0 text-teal-light" />
                  <span>
                    @lucentiasocial<span className="sr-only"> {tCommon("opensNewTab")}</span>
                  </span>
                </a>
              </li>
              <li>
                <a
                  href={LEGAL_MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-start gap-3 transition-[color,translate] duration-300 hover:text-white motion-safe:hover:translate-x-0.5"
                >
                  <MapPin aria-hidden="true" className="mt-1 size-4 shrink-0 text-teal-light" />
                  <address className="not-italic">
                    {LEGAL_ADDRESS.street}
                    <br />
                    {LEGAL_ADDRESS.postalCode} {LEGAL_ADDRESS.city}
                    <span className="sr-only"> {tCommon("opensNewTab")}</span>
                  </address>
                </a>
              </li>
              <li className="inline-flex items-center gap-3">
                <Clock aria-hidden="true" className="size-4 shrink-0 text-teal-light" />
                {tNav("hours")}
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col gap-6 border-t border-white/12 pt-8 lg:flex-row lg:items-center lg:justify-between">
          <nav aria-label={tNav("legalNav")}>
            <ul className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
              {legal.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="text-on-navy-muted transition-colors hover:text-white">
                    {item.label}
                  </Link>
                </li>
              ))}
              <li>
                <button
                  type="button"
                  onClick={resetCookieConsent}
                  className="text-on-navy-muted underline decoration-white/30 underline-offset-4 transition-colors hover:text-white"
                >
                  {tNav("cookieSettings")}
                </button>
              </li>
            </ul>
          </nav>
          <div className="flex items-center justify-between gap-6 lg:justify-end">
            <small className="text-sm text-on-navy-muted">{tNav("copyright", { year })}</small>
            <LanguageToggle variant="navy" />
          </div>
        </div>
      </div>
    </footer>
  );
}
